package com.arena_web.ui.sections.profile

import androidx.compose.runtime.*
import com.arena_web.data.dto.*
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.cards.PlayerStat
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.icons.TintedGlyph
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.profile.edit.InterestCatalog
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin
import kotlin.math.sqrt
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

// Per Figma "Border/inner/L" — Linear Gradient with 70% layer opacity over
// #29348A33 (= rgba(41,52,138,0.20)) → #0357E633 (= rgba(3,87,230,0.20)).
// Final rendered alphas after the 70% layer multiplier: 0.14 / 0.14.
private const val OUTER_BORDER_GRADIENT =
    "linear-gradient(-84deg, rgba(41, 52, 138, 0.14), rgba(3, 87, 230, 0.14))"
// Per Figma "Surface/gradient/01" — Linear Gradient with 30% layer opacity:
// stop 1: #3C79F1 @ 45% opacity → 0.135 alpha, position 42%
// stop 2: #05040C @ 10% opacity → 0.03 alpha, position 75%
private const val OUTER_BG =
    "linear-gradient(-165deg, rgba(60, 121, 241, 0.235) 32%, rgba(5, 4, 12, 0.03) 75%)"
// Per Figma "Border/inner/L" — Linear Gradient with 70% layer opacity over
// #29348A33 (= rgba(41,52,138,0.20)) → #0357E633 (= rgba(3,87,230,0.20)).
// Final rendered alphas after the 70% layer multiplier: 0.14 / 0.14.
private const val INNER_BORDER_GRADIENT =
    "linear-gradient(-84deg, rgba(41, 52, 138, 0.14), rgba(3, 87, 230, 0.14))"
// Per Figma "Surface/inner/gradient_L" — Linear Gradient with 15% layer opacity over
// #003BAE33 (= rgba(0,59,174,0.20)) → #0357E633 (= rgba(3,87,230,0.20)).
// Final rendered alphas after the 15% layer multiplier: 0.03 / 0.03.
private const val INNER_BG =
    "linear-gradient(-115deg, rgba(0, 59, 174, 0.03), rgba(3, 87, 230, 0.03))"
// Pill / chip / interactive-outline buttons use the project's design-system tokens:
// border  → AppGradients.ButtonBorderGradientPrimary via the mask-composite overlay below
// surface → AppGradients.InteractiveSecondary

@Composable
fun OverviewSidebar(
    user: UserDto?,
    sportProfile: SportProfileDto?,
    /** Cross-sport aggregate populated when the header sport is "All".
     *  Used for the FIFA OVR display on the card carousel; stats and
     *  position stay blank since they're sport-specific. */
    aggregateRating: SportProfileAggregateDto? = null,
    followersCount: Long? = null,
    followingCount: Long? = null,
    isOwnProfile: Boolean = true,
    isFollowing: Boolean = false,
    onFollowClick: (() -> Unit)? = null,
    onChatClick: (() -> Unit)? = null,
    onEditClick: (() -> Unit)? = null,
    onEditPersonalInfo: (() -> Unit)? = null,
    onEditFocus: (() -> Unit)? = null,
    onFollowingClick: (() -> Unit)? = null,
    onFollowersClick: (() -> Unit)? = null,
    onSyncCardsClick: (() -> Unit)? = null,
    onEditCardClick: (() -> Unit)? = null,
    profileCards: List<ProfileCardDto> = emptyList(),
    languages: List<String> = emptyList(),
    interestSports: Set<String> = emptySet(),
    interestActivities: Set<String> = emptySet(),
    interestLifestyle: Set<String> = emptySet(),
    interestNotes: Map<String, String> = emptyMap(),
    onInterestNoteClick: ((String) -> Unit)? = null,
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    val sportName = Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase() ?: "all"
    // On own profile, the carousel cards come from the dedicated profileCards
    // list (display-ordered). Other users only ship the resolved main card on
    // the User DTO, so synthesize a single-entry list for them.
    val effectiveCards: List<ProfileCardDto> = when {
        profileCards.isNotEmpty() -> profileCards
        user?.mainProfileCard != null -> listOf(user.mainProfileCard)
        else -> emptyList()
    }
    val cardStats = sportProfile?.ratings
        ?.sortedBy { it.attribute.displayOrder }
        ?.map { PlayerStat(it.value.toInt(), it.attribute.name.take(3).uppercase()) }
        ?: emptyList()

    Column(
        modifier = modifier
            .fillMaxHeight()
            .sBorderRadius(40)
            .overflow(Overflow.Hidden)
            .styleModifier {
                property("backdrop-filter", "blur(${s(11.165)})")
                property("-webkit-backdrop-filter", "blur(${s(11.165)})")
                property("background", OUTER_BG)
                property("position", "relative")
            }
    ) {
        // Gradient border ring — mask-composite trick so the gradient follows the radius-40 corners.
        Box(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property("border", "1px solid transparent")
                    property("border-radius", s(40))
                    property("background", "$OUTER_BORDER_GRADIENT border-box")
                    property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("-webkit-mask-composite", "xor")
                    property("mask-composite", "exclude")
                    property("pointer-events", "none")
                }
        )
        // Inner padding 18, gap 12
        Column(
            modifier = Modifier
                .fillMaxSize()
                .sPadding(18)
                .sGap(12)
        ) {
            // Header row: Overview + expand icon (44x44 wrap, 24x24 icon per Figma)
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    t.profileSidebarTitle,
                    modifier = Modifier
                        .sFontSize(24)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(32)
                        .sLetterSpacing(0.24)
                        .color(AppColors.Text.Secondary)
                        .flexGrow(1)
                )
                Box(
                    modifier = Modifier
                        .sSize(44)
                        .cursor(Cursor.Pointer)
                        .onClick { onEditClick?.invoke() },
                    contentAlignment = Alignment.Center
                ) {
                    Icon(src = AppIcons.ExpandProfile, modifier = Modifier.sSize(24))
                }
            }

            // Main 2-column content (50/50)
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .flexGrow(1)
                    .minHeight(0.px)
                    .sGap(8)
                    .styleModifier { property("align-items", "stretch") }
            ) {
                // Left half: FUT card + Following/Followers + Player Honors
                Column(
                    modifier = Modifier
                        .sGap(8)
                        .styleModifier { property("flex", "1 1 0") }
                ) {
                    PlayerCardPanel(
                        user = user,
                        sportProfile = sportProfile,
                        aggregateRating = aggregateRating,
                        sportName = sportName,
                        profileCards = effectiveCards,
                        cardStats = cardStats,
                        isOwnProfile = isOwnProfile,
                        followingCount = followingCount,
                        followersCount = followersCount,
                        onFollowingClick = onFollowingClick,
                        onFollowersClick = onFollowersClick,
                        onSyncClick = onSyncCardsClick,
                        onEditClick = onEditCardClick,
                        modifier = Modifier
                    )
                    PlayerHonorsPanel(
                        modifier = Modifier.flexGrow(1)
                    )
                }
                // Right half: Basic Info + Interests
                Column(
                    modifier = Modifier
                        .sGap(8)
                        .styleModifier { property("flex", "1 1 0") }
                ) {
                    BasicInfoPanel(
                        user = user,
                        isOwnProfile = isOwnProfile,
                        languages = languages,
                        onEditClick = onEditPersonalInfo
                    )
                    InterestsPanel(
                        isOwnProfile = isOwnProfile,
                        isFollowing = isFollowing,
                        onFollowClick = onFollowClick,
                        onChatClick = onChatClick,
                        onEditClick = onEditFocus,
                        interestSports = interestSports,
                        interestActivities = interestActivities,
                        interestLifestyle = interestLifestyle,
                        interestNotes = interestNotes,
                        onInterestNoteClick = onInterestNoteClick,
                        modifier = Modifier.flexGrow(1)
                    )
                }
            }
        }
    }
}

// =============================================================================
// Player Card panel (left-top): FUT card carousel + Following/Followers row
// Renders profileCards as a stacked carousel — the front card mirrors what is
// considered "main" everywhere else (posts, events, …). Sync icon (top-left)
// opens the reorder dialog; pencil (top-right) opens the avatar/template editor.
// =============================================================================
@Composable
private fun PlayerCardPanel(
    user: UserDto?,
    sportProfile: SportProfileDto?,
    aggregateRating: SportProfileAggregateDto? = null,
    sportName: String,
    profileCards: List<ProfileCardDto>,
    cardStats: List<PlayerStat>,
    isOwnProfile: Boolean,
    followingCount: Long?,
    followersCount: Long?,
    onFollowingClick: (() -> Unit)? = null,
    onFollowersClick: (() -> Unit)? = null,
    onSyncClick: (() -> Unit)? = null,
    onEditClick: (() -> Unit)? = null,
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    // The first card in the gallery is the main one. The carousel preview lets
    // users glance at their other cards without leaving the profile.
    var currentIndex by remember(profileCards.size) { mutableStateOf(0) }
    val safeIndex = currentIndex.coerceIn(0, (profileCards.size - 1).coerceAtLeast(0))
    val currentCard = profileCards.getOrNull(safeIndex)

    // Front/back flip — kept here so the flip icon in the toolbar can drive
    // the carousel below. Resets to front whenever the active card changes
    // so a previously-flipped slot doesn't carry over to a different card.
    var isFlipped by remember(safeIndex) { mutableStateOf(false) }

    Column(
        modifier = modifier
            .fillMaxWidth()
            .sBorderRadius(16)
            .overflow(Overflow.Hidden)
            .styleModifier {
                property("background", INNER_BG)
                property("position", "relative")
            }
            .sPaddingBottom(12)
            .sGap(8),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        InnerPanelBorderRing()
        // Card row — flip icon on the left, carousel centered, pencil on the
        // right. Icons sit at the top edge of the card (Alignment.Top) so they
        // mirror the Figma layout where the pencil overlaps the card corner.
        // Top padding is tight (10px) so the card starts near the panel top.
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .sGap(4)
                .styleModifier { property("padding", "${s(10)} ${s(10)} 0") },
            verticalAlignment = Alignment.Top,
        ) {
            // Flip toggle — front shows the card art + name; back shows the
            // radar with overall rating + stat values. The icon itself rotates
            // 180° on each click so the affordance reads as "in motion".
            Box(
                modifier = Modifier
                    .sSize(32)
                    .cursor(Cursor.Pointer)
                    .onClick { isFlipped = !isFlipped }
                    .styleModifier {
                        property("transition", "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)")
                        property("transform", if (isFlipped) "rotate(180deg)" else "rotate(0deg)")
                        property("flex-shrink", "0")
                    },
                contentAlignment = Alignment.Center
            ) {
                Icon(src = AppIcons.CardFlip, modifier = Modifier.sSize(24))
            }

            // Carousel takes remaining width; the card itself is fixed-size
            // and self-centered by the carousel's inner Box. The extra
            // `padding-top` only pushes the card down — flip/pencil icons
            // stay aligned with the panel's top edge.
            Box(
                modifier = Modifier.styleModifier {
                    property("flex", "1 1 0")
                    property("padding-top", s(22))
                },
                contentAlignment = Alignment.Center
            ) {
                // FIFA OVR: per-sport rating when a sport is selected;
                // cross-sport mean when "All" — both 1.0–5.0, scaled × 20
                // by PlayerCard for the 0–100 badge.
                val ovr =
                    sportProfile?.overallRating
                        ?: aggregateRating?.overallRating
                        ?: 0.0
                CardCarousel(
                    playerName = (user?.fullName?.trim()?.split(" ")?.firstOrNull()
                        ?.takeIf { it.isNotBlank() } ?: user?.username ?: "").uppercase(),
                    rating = ovr.toInt(),
                    position = sportProfile?.position?.name ?: "",
                    sportName = sportName,
                    stats = cardStats,
                    // "Has profile" in the carousel's eyes — either a real
                    // per-sport profile, or the cross-sport aggregate that
                    // unlocks the OVR badge in "All" mode.
                    hasSportProfile = sportProfile != null || aggregateRating != null,
                    cards = profileCards,
                    currentIndex = safeIndex,
                    currentCard = currentCard,
                    isFlipped = isFlipped,
                    onNext = {
                        if (profileCards.size > 1) {
                            currentIndex = (safeIndex + 1) % profileCards.size
                        }
                    },
                    onPrev = {
                        if (profileCards.size > 1) {
                            currentIndex = if (safeIndex == 0) profileCards.size - 1
                                else safeIndex - 1
                        }
                    },
                )
            }

            if (isOwnProfile) {
                Box(
                    modifier = Modifier
                        .sSize(32)
                        .cursor(Cursor.Pointer)
                        .onClick { onEditClick?.invoke() }
                        .styleModifier { property("flex-shrink", "0") },
                    contentAlignment = Alignment.Center
                ) {
                    Icon(src = AppIcons.Pencil, modifier = Modifier.sSize(24))
                }
            } else {
                // Mirror the flip icon's footprint so the carousel stays
                // visually centered when there's no pencil on the right.
                Box(modifier = Modifier.sSize(32).styleModifier { property("flex-shrink", "0") })
            }
        }

        // Pagination dots — only when there's more than one card to browse.
        if (profileCards.size > 1) {
            val activeRgb = LocalSportColors.current.primary.toRgb().let {
                "rgb(${it.red}, ${it.green}, ${it.blue})"
            }
            Row(
                modifier = Modifier.sGap(4),
                verticalAlignment = Alignment.CenterVertically
            ) {
                profileCards.forEachIndexed { idx, _ ->
                    val active = idx == safeIndex
                    Box(
                        modifier = Modifier
                            .sSize(if (active) 6 else 4)
                            .sBorderRadius(100)
                            .cursor(Cursor.Pointer)
                            .onClick { currentIndex = idx }
                            .styleModifier {
                                property("background-color", if (active) activeRgb else "#091a40")
                            }
                    )
                }
            }
        }

        // Following / Followers pills — reserve the 36px row even before counts
        // arrive from backend so the layout doesn't shift when data lands.
        // Extra top padding (on top of the parent Column's sGap(8)) opens up
        // breathing room between the pagination dots and the pills.
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .sHeight(36)
                .sGap(8)
                .sPaddingTop(10)
                .styleModifier { property("padding-left", s(12)); property("padding-right", s(12)) },
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (followingCount != null && followersCount != null) {
                CountPill(
                    label = t.profileSidebarFollowing,
                    count = followingCount,
                    onClick = onFollowingClick,
                    modifier = Modifier.flexGrow(1)
                )
                CountPill(
                    label = t.profileSidebarFollowers,
                    count = followersCount,
                    onClick = onFollowersClick,
                    modifier = Modifier.flexGrow(1)
                )
            }
        }
    }
}

@Composable
internal fun CardCarousel(
    playerName: String,
    rating: Int,
    position: String,
    sportName: String,
    stats: List<PlayerStat>,
    hasSportProfile: Boolean,
    cards: List<ProfileCardDto>,
    currentIndex: Int,
    currentCard: ProfileCardDto?,
    isFlipped: Boolean,
    onNext: () -> Unit = {},
    onPrev: () -> Unit = {},
) {
    // Back-stack: closest peek = next card; farthest peek = previous card.
    // Using `currentIndex-1` for the back slot means a card the user just
    // swiped past lands at the bottom of the visible deck on the next render
    // — the "swiped card goes to the back" deck behaviour.
    val peeks: List<ProfileCardDto> = when {
        cards.size <= 1 -> emptyList()
        cards.size == 2 -> listOf(cards[(currentIndex + 1) % cards.size])
        else -> listOf(
            cards[(currentIndex + 1) % cards.size],
            cards[(currentIndex - 1 + cards.size) % cards.size]
        )
    }

    // Drag progress fed up from SwipeableCard so the peek stack can promote
    // toward the active position while the user is mid-swipe. `progress` is
    // 0..1 (capped at the swipe threshold), `peekDragging` mirrors the active
    // dragging flag so we can disable peek transitions while the finger is
    // down (instant follow) and animate them back when released.
    var peekProgress by remember { mutableStateOf(0.0) }
    var peekDragging by remember { mutableStateOf(false) }

    Box(contentAlignment = Alignment.Center) {
        // Peek cards — render farthest first so they layer correctly behind
        // the front card. While the user drags the active card, each peek
        // smoothly interpolates from its resting offset/scale/opacity toward
        // the position of the slot ABOVE it (depth-1). Peeks always show the
        // face-up frame regardless of the active card's flip state.
        peeks.reversed().forEachIndexed { reverseIdx, peek ->
            val depth = peeks.size - reverseIdx
            val baseTx = depth * 14.0
            val baseScale = 1.0 - (depth * 0.08)
            val baseOpacity = (0.55 - (depth - 1) * 0.2).coerceAtLeast(0.15)
            // "Promoted" target = slot at depth-1 (visually one step closer
            // to the active card). For the nearest peek (depth=1) the target
            // is the active slot itself (tx 0, scale 1, opacity 1).
            val promotedTx = (depth - 1) * 14.0
            val promotedScale = 1.0 - ((depth - 1) * 0.08)
            val promotedOpacity = if (depth == 1) 1.0
                else (0.55 - (depth - 2) * 0.2).coerceAtLeast(0.15)
            // Ease-in cubic — peek barely moves at the start of the drag and
            // accelerates toward the promoted position near the swipe
            // threshold. Makes early-drag growth feel gentle rather than
            // jumpy.
            val t = peekProgress * peekProgress * peekProgress
            val tx = baseTx + (promotedTx - baseTx) * t
            val sc = baseScale + (promotedScale - baseScale) * t
            val op = baseOpacity + (promotedOpacity - baseOpacity) * t
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("position", "absolute")
                        property("transform", "translateX(${tx}px) scale($sc)")
                        property("transform-origin", "center")
                        property("opacity", "$op")
                        property("pointer-events", "none")
                        property("z-index", "${-depth}")
                        property(
                            "transition",
                            if (peekDragging) "none"
                            else "transform 0.36s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.3s ease"
                        )
                        property("will-change", "transform, opacity")
                    }
            ) {
                PlayerCard(
                    playerName = playerName,
                    rating = rating,
                    position = position,
                    sport = sportName,
                    size = PlayerCardSize.Large,
                    avatarUrl = if (hasSportProfile) peek.avatarUrl ?: AppIcons.EmptyAvatar else null,
                    hideRating = true,
                    stats = emptyList(),
                    cardAssetsBasePath = peek.assetsBasePath,
                    cardTextColor = peek.textColor,
                )
            }
        }
        // Front + back faces wrapped in a 3D flip container. Outer SwipeableCard
        // captures horizontal drag — swipe-left advances to the next card,
        // swipe-right backs to the previous one (no-op when only 1 card).
        // `onDragProgress` feeds dragX magnitude (clamped 0..1 over threshold)
        // and the live dragging flag back to drive the peek stack above.
        SwipeableCard(
            enabled = cards.size > 1,
            onNext = onNext,
            onPrev = onPrev,
            onDragProgress = { p, d ->
                peekProgress = p
                peekDragging = d
            },
        ) {
            FlippableCard(
                isFlipped = isFlipped,
                front = {
                    PlayerCard(
                        playerName = playerName,
                        rating = rating,
                        position = position,
                        sport = sportName,
                        size = PlayerCardSize.Large,
                        avatarUrl = if (hasSportProfile) currentCard?.avatarUrl ?: AppIcons.EmptyAvatar else null,
                        hideRating = true,
                        stats = emptyList(),
                        cardAssetsBasePath = currentCard?.assetsBasePath,
                        cardTextColor = currentCard?.textColor,
                    )
                },
                back = {
                    PlayerCardBack(
                        rating = rating,
                        position = position,
                        sportName = sportName,
                        stats = stats,
                        cardAssetsBasePath = currentCard?.assetsBasePath,
                        cardTextColor = currentCard?.textColor,
                    )
                },
            )
        }
    }
}

// =============================================================================
// Swipeable wrapper — captures horizontal pointer drag and translates only the
// child (active card) for live visual feedback. On release past [threshold]
// the matching nav callback fires; otherwise the card snaps back. Pointer
// events give us touch + mouse + trackpad with one set of handlers.
// `rememberUpdatedState` keeps the DOM listeners pointing at the LATEST
// onNext/onPrev so repeated swipes don't get stuck on stale `safeIndex`.
// =============================================================================
@Composable
internal fun SwipeableCard(
    enabled: Boolean,
    onNext: () -> Unit,
    onPrev: () -> Unit,
    onDragProgress: (progress: Double, dragging: Boolean) -> Unit = { _, _ -> },
    threshold: Double = 50.0,
    content: @Composable () -> Unit,
) {
    if (!enabled) {
        content()
        return
    }
    val onNextRef = rememberUpdatedState(onNext)
    val onPrevRef = rememberUpdatedState(onPrev)
    val onDragProgressRef = rememberUpdatedState(onDragProgress)

    var dragX by remember { mutableStateOf(0.0) }
    var dragging by remember { mutableStateOf(false) }
    // -1 = flying off to the left (next), +1 = flying off to the right (prev),
    // 0 = idle / dragging. Used to drive the dramatic "dealt from deck"
    // exit animation before the index swap.
    var flying by remember { mutableStateOf(0) }
    // 0 = idle, 1/2/3 = keyframe stages of the arc flight. Stepping through
    // these triggers CSS transitions between each stage's transform values so
    // the card traces a curved path on its way back to the deck.
    var flyPhase by remember { mutableStateOf(0) }
    // One-shot guard that disables the CSS transition for exactly the frame
    // when we reset transform back to neutral after the index swap, so the
    // new active card pops in at centre instead of sliding from off-screen.
    var skipTransition by remember { mutableStateOf(false) }
    val startX = remember { mutableStateOf(0.0) }
    val activePointer = remember { mutableStateOf<Int?>(null) }
    val scope = rememberCoroutineScope()

    val isFlying = flying != 0

    // Drag-only feedback values.
    val clampedX = dragX.coerceIn(-180.0, 180.0)
    val dragRotate = clampedX * 0.05
    val dragScale = 1.0 - (kotlin.math.abs(clampedX) / 2400.0).coerceAtMost(0.04)

    // Effective transform values. While flying, sweep horizontally to the
    // side and back: 1) move out in the swipe direction, 2) peak far side,
    // 3) cross back behind the deck (z-index drops), 4) settle into the
    // back-of-deck slot. Vertical position stays at 0 throughout.
    val tx = when {
        !isFlying -> clampedX
        flyPhase == 1 -> flying.toDouble() * 220.0     // sweep out
        flyPhase == 2 -> flying.toDouble() * 320.0     // peak: far side
        flyPhase == 3 -> flying.toDouble() * 180.0     // crossing back behind
        else -> 28.0                                   // settled at peek-2 X
    }
    val ty = 0.0
    val rot = when {
        !isFlying -> dragRotate
        flyPhase == 1 -> flying.toDouble() * 12.0
        flyPhase == 2 -> flying.toDouble() * 32.0
        flyPhase == 3 -> flying.toDouble() * 22.0
        else -> 0.0
    }
    val sc = when {
        !isFlying -> dragScale
        flyPhase == 1 -> 0.94
        flyPhase == 2 -> 0.84
        flyPhase == 3 -> 0.68
        else -> 0.55
    }
    val op = when {
        !isFlying -> 1.0
        flyPhase == 1 -> 1.0
        flyPhase == 2 -> 0.85
        flyPhase == 3 -> 0.4
        else -> 0.0
    }
    // z-index goes negative for stages 3+ so the card slides BEHIND the
    // existing peek stack (peeks live at z-index -1 / -2).
    val zIndex = when {
        !isFlying -> "auto"
        flyPhase >= 3 -> "-5"
        else -> "2"
    }

    Div(attrs = {
        style {
            property("display", "inline-block")
            property(
                "transform",
                "translate(${tx}px, ${ty}px) rotate(${rot}deg) scale($sc)"
            )
            property("transform-origin", "center 80%")
            property("opacity", "$op")
            property("z-index", zIndex)
            property(
                "transition",
                when {
                    dragging || skipTransition -> "none"
                    // Each fly stage runs ~190ms so the 4-stage loop takes
                    // ~760ms — slow enough that the circular path reads
                    // clearly without feeling stuck.
                    isFlying -> "transform 0.19s ease-in-out, opacity 0.19s ease-in-out"
                    else -> "transform 0.36s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.2s"
                }
            )
            property("touch-action", "pan-y")
            property("user-select", "none")
            property("-webkit-user-select", "none")
            property("cursor", if (dragging) "grabbing" else "grab")
            property("will-change", "transform, opacity")
        }
        ref { el ->
            fun notifyProgress() {
                val p = (kotlin.math.abs(dragX) / threshold).coerceIn(0.0, 1.0)
                onDragProgressRef.value(p, dragging)
            }
            val onDown: (org.w3c.dom.events.Event) -> Unit = { ev ->
                // Ignore new pointers while the card is mid-flight.
                if (!isFlying) {
                    val pe = ev.unsafeCast<org.w3c.dom.pointerevents.PointerEvent>()
                    dragging = true
                    startX.value = pe.clientX.toDouble()
                    dragX = 0.0
                    activePointer.value = pe.pointerId
                    runCatching { el.setPointerCapture(pe.pointerId) }
                    notifyProgress()
                }
            }
            val onMove: (org.w3c.dom.events.Event) -> Unit = { ev ->
                if (dragging) {
                    val pe = ev.unsafeCast<org.w3c.dom.pointerevents.PointerEvent>()
                    if (activePointer.value == null || activePointer.value == pe.pointerId) {
                        dragX = pe.clientX.toDouble() - startX.value
                        notifyProgress()
                    }
                }
            }
            val onUp: (org.w3c.dom.events.Event) -> Unit = { ev ->
                if (dragging) {
                    val pe = ev.unsafeCast<org.w3c.dom.pointerevents.PointerEvent>()
                    dragging = false
                    activePointer.value = null
                    runCatching { el.releasePointerCapture(pe.pointerId) }
                    when {
                        dragX <= -threshold -> {
                            // Hold the peek stack at the FULLY PROMOTED state
                            // while the active card loops away. That keeps
                            // the next peek docked at the centre, so when the
                            // swap fires there's no "snap back and forward"
                            // for the new active.
                            flying = -1
                            flyPhase = 1
                            onDragProgressRef.value(1.0, false)
                            scope.launch {
                                delay(190); flyPhase = 2
                                delay(190); flyPhase = 3
                                delay(190); flyPhase = 4
                                delay(190)
                                // Freeze peek transitions for the swap so the
                                // new content snaps into base positions
                                // instantly (no rise-and-settle).
                                onDragProgressRef.value(0.0, true)
                                onNextRef.value.invoke()
                                skipTransition = true
                                flying = 0
                                flyPhase = 0
                                dragX = 0.0
                                delay(20)
                                skipTransition = false
                                onDragProgressRef.value(0.0, false)
                            }
                        }
                        dragX >= threshold -> {
                            flying = 1
                            flyPhase = 1
                            onDragProgressRef.value(1.0, false)
                            scope.launch {
                                delay(190); flyPhase = 2
                                delay(190); flyPhase = 3
                                delay(190); flyPhase = 4
                                delay(190)
                                onDragProgressRef.value(0.0, true)
                                onPrevRef.value.invoke()
                                skipTransition = true
                                flying = 0
                                flyPhase = 0
                                dragX = 0.0
                                delay(20)
                                skipTransition = false
                                onDragProgressRef.value(0.0, false)
                            }
                        }
                        else -> {
                            dragX = 0.0
                            // Animate peek stack back to resting state via the
                            // CSS transition on the peek boxes.
                            onDragProgressRef.value(0.0, false)
                        }
                    }
                }
            }
            el.addEventListener("pointerdown", onDown)
            el.addEventListener("pointermove", onMove)
            el.addEventListener("pointerup", onUp)
            el.addEventListener("pointercancel", onUp)
            el.addEventListener("pointerleave", onUp)
            onDispose {
                el.removeEventListener("pointerdown", onDown)
                el.removeEventListener("pointermove", onMove)
                el.removeEventListener("pointerup", onUp)
                el.removeEventListener("pointercancel", onUp)
                el.removeEventListener("pointerleave", onUp)
            }
        }
    }) {
        content()
    }
}

// =============================================================================
// 3D flip container — perspective on the outer wrapper, preserve-3d on the
// inner pivot. Both faces use backface-visibility:hidden so the back never
// shows through the front. Width/height match Large card.
// =============================================================================
@Composable
internal fun FlippableCard(
    isFlipped: Boolean,
    front: @Composable () -> Unit,
    back: @Composable () -> Unit,
) {
    val w = PlayerCardSize.Large.w
    val h = PlayerCardSize.Large.h
    Box(
        modifier = Modifier
            .sWidth(w)
            .sHeight(h)
            .styleModifier {
                property("position", "relative")
                property("perspective", "1200px")
            }
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("position", "relative")
                    property("transform-style", "preserve-3d")
                    property("transition", "transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)")
                    property("transform", if (isFlipped) "rotateY(180deg)" else "rotateY(0deg)")
                }
        ) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .styleModifier {
                        property("position", "absolute")
                        property("inset", "0")
                        property("backface-visibility", "hidden")
                        property("-webkit-backface-visibility", "hidden")
                    },
                contentAlignment = Alignment.Center
            ) {
                front()
            }
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .styleModifier {
                        property("position", "absolute")
                        property("inset", "0")
                        property("backface-visibility", "hidden")
                        property("-webkit-backface-visibility", "hidden")
                        property("transform", "rotateY(180deg)")
                    },
                contentAlignment = Alignment.Center
            ) {
                back()
            }
        }
    }
}

// =============================================================================
// Back side (Figma 7246:539973) — the same card frame as the front, overlaid
// with a hexagonal radar, six stat icons + values around it, and the overall
// rating + position abbreviation at the bottom. Positions follow the Figma
// 247×369 grid expressed as percentages of the actual 254×385 PlayerCard so
// scaling stays consistent with the front card.
// =============================================================================
@Composable
internal fun PlayerCardBack(
    rating: Int,
    position: String,
    sportName: String,
    stats: List<PlayerStat>,
    cardAssetsBasePath: String?,
    cardTextColor: String?,
) {
    val sportColors = LocalSportColors.current
    // Resolve the back-side accent (rating value + the divider line above it).
    // Defaults to the sport's `metricsPrimary`, but a non-blank `cardTextColor`
    // wins so a tennis card paints both the rating and the divider in green,
    // matching the front-side template tint.
    val backAccent = remember(cardTextColor, sportColors) {
        if (cardTextColor.isNullOrBlank()) {
            sportColors.metricsPrimary
        } else {
            val hex = cardTextColor.removePrefix("#")
            com.varabyte.kobweb.compose.ui.graphics.Color.rgb(
                hex.substring(0, 2).toInt(16),
                hex.substring(2, 4).toInt(16),
                hex.substring(4, 6).toInt(16),
            )
        }
    }
    // 6 stats clockwise from top-center, matching Figma ordering. Falls back
    // to "—" when the slot is empty so the layout doesn't collapse before the
    // sport profile loads. The icon now follows the *attribute* (Striking,
    // Accuracy, …) — not the slot position — via AppIcons.statIconFor, so the
    // same attribute always renders with the same symbol regardless of sport.
    val statSlots = (0 until 6).map { i ->
        val s = stats.getOrNull(i)
        val icon = if (s != null) AppIcons.statIconFor(s.label) else AppIcons.StatTarget
        icon to s?.value
    }
    Box(
        modifier = Modifier
            .sWidth(PlayerCardSize.Large.w)
            .sHeight(PlayerCardSize.Large.h)
            .styleModifier { property("position", "relative") }
    ) {
        // Reuse PlayerCard purely as the frame art (everything else suppressed).
        PlayerCard(
            playerName = "",
            rating = 0,
            position = "",
            sport = sportName,
            size = PlayerCardSize.Large,
            avatarUrl = null,
            hideRating = true,
            hideShadow = true,
            stats = emptyList(),
            cardAssetsBasePath = cardAssetsBasePath,
            cardTextColor = cardTextColor,
        )

        // Data-driven hexagonal radar — vertices scale by stat value/100, the
        // outer reference hex stays at full radius. Sized to Figma's 118×141
        // footprint and positioned at (61.78, 77) — i.e. horizontally centered
        // on the card with the stat icons sitting just outside its vertices.
        BackAbsolute(
            leftPct = 61.78 / 247,
            topPct = 77.0 / 369,
            widthPct = 118.28 / 247,
            heightPct = 141.41 / 369,
        ) {
            BackRadarSvg(stats = stats)
        }

        // Stat slot 0 — top center
        StatSlot(leftPct = 109.0 / 247, topPct = 33.0 / 369, slot = statSlots[0])
        // Stat slot 1 — top right
        StatSlot(leftPct = 180.0 / 247, topPct = 104.0 / 369, slot = statSlots[1])
        // Stat slot 2 — bottom right
        StatSlot(leftPct = 181.0 / 247, topPct = 164.0 / 369, slot = statSlots[2])
        // Stat slot 3 — bottom center
        StatSlot(leftPct = 109.0 / 247, topPct = 218.0 / 369, slot = statSlots[3])
        // Stat slot 4 — bottom left
        StatSlot(leftPct = 35.0 / 247, topPct = 160.0 / 369, slot = statSlots[4])
        // Stat slot 5 — top left
        StatSlot(leftPct = 35.0 / 247, topPct = 102.0 / 369, slot = statSlots[5])

        // Thin divider line above the rating stack — Figma 7246:540028 /
        // 7314:203916. 60px wide, ~0.8px tall, centred horizontally, sits
        // just below the radar and above the rating block (top = 277/369).
        // Horizontal gradient that fades from 10% → 100% → 10% alpha so the
        // line dissolves into the card art at both ends.
        BackAbsolute(
            leftPct = 93.0 / 247.0,
            topPct = 277.0 / 369.0,
            widthPct = 60.0 / 247.0,
        ) {
            val rgb = backAccent.toRgb()
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .sHeight(1)
                    .styleModifier {
                        property(
                            "background",
                            "linear-gradient(90deg, rgba(${rgb.red},${rgb.green},${rgb.blue},0.1) 0%, rgba(${rgb.red},${rgb.green},${rgb.blue},1) 50%, rgba(${rgb.red},${rgb.green},${rgb.blue},0.1) 100%)"
                        )
                        property("border-radius", "0.4px")
                    }
            )
        }

        // Rating + position stack — Figma 7246:539975. Anchored to the BOTTOM
        // of the card (top = 286/369, left = 105/247), not the radar centre,
        // matching the back-side composition. 37×47 footprint.
        BackAbsolute(
            leftPct = 105.0 / 247.0,
            topPct = 286.0 / 369.0,
            widthPct = 37.0 / 247.0,
            heightPct = 47.0 / 369.0,
        ) {
            BackRatingStack(
                rating = rating,
                position = position,
                color = backAccent,
            )
        }
    }
}

/**
 * Figma 7246:539975 — rating displayed standalone on the back of the player
 * card. Position label and the small divider line are intentionally hidden
 * for now (per product call); the field is wired so they can be brought
 * back without re-positioning the parent BackAbsolute.
 */
@Composable
private fun BackRatingStack(
    rating: Int,
    @Suppress("UNUSED_PARAMETER") position: String,
    color: com.varabyte.kobweb.compose.ui.graphics.Color,
) {
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            "$rating",
            modifier = Modifier
                .color(color)
                .sFontSize(34)
                .sLineHeight(38)
                .fontWeight(AppTypography.SemiBold)
                .styleModifier {
                    property("font-family", "'SF Pro Display', sans-serif")
                    property("text-align", "center")
                    property("letter-spacing", "0.34px")
                    property("text-transform", "uppercase")
                }
        )
    }
}

// =============================================================================
// Data-driven back-side radar. Vertical hexagon (pointy top/bottom): vertices
// at -90°, -30°, 30°, 90°, 150°, 210° from the radar centre. The outer hex
// stays at full radius as a "max" reference; the inner data polygon scales
// each vertex by stat value/100. Built as inline SVG and injected via
// `innerHTML` — same pattern PlayerOverviewSection uses for its radar.
// =============================================================================
@Composable
private fun BackRadarSvg(stats: List<PlayerStat>) {
    val w = 118.28
    val h = 141.41
    val cx = w / 2.0
    val cy = h / 2.0
    // Radius chosen so the upper-right / lower-right / upper-left / lower-left
    // vertices touch the viewBox left/right edges. Top/bottom vertices sit at
    // y = cy ± r, with small padding inside the 141.41 viewBox height.
    val r = w / sqrt(3.0)

    val angles = listOf(-90.0, -30.0, 30.0, 90.0, 150.0, 210.0)
    fun deg2rad(d: Double) = d * PI / 180.0

    val outer = angles.map { d ->
        val a = deg2rad(d)
        (cx + r * cos(a)) to (cy + r * sin(a))
    }
    val data = angles.mapIndexed { i, d ->
        val a = deg2rad(d)
        val v = (stats.getOrNull(i)?.value?.toDouble() ?: 0.0).coerceIn(0.0, 100.0)
        val f = v / 100.0
        (cx + r * f * cos(a)) to (cy + r * f * sin(a))
    }
    val outerPts = outer.joinToString(" ") { "${it.first},${it.second}" }
    val dataPts = data.joinToString(" ") { "${it.first},${it.second}" }

    val svg = buildString {
        append("<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 $w $h\" ")
        append("width=\"100%\" height=\"100%\" overflow=\"visible\">")
        // Outer reference hex
        append("<polygon points=\"$outerPts\" fill=\"none\" stroke=\"#72A5FF\" stroke-width=\"0.96\"/>")
        // Inner data polygon
        append("<polygon points=\"$dataPts\" fill=\"#0061FF\" fill-opacity=\"0.18\" ")
        append("stroke=\"#1E7FF5\" stroke-width=\"0.8\" stroke-linejoin=\"round\"/>")
        // Centre dot
        append("<ellipse cx=\"$cx\" cy=\"$cy\" rx=\"1.5\" ry=\"1.4\" fill=\"white\"/>")
        // Outer vertex dots
        outer.forEach { (x, y) ->
            append("<ellipse cx=\"$x\" cy=\"$y\" rx=\"1.66\" ry=\"1.54\" fill=\"white\"/>")
        }
        append("</svg>")
    }

    // `innerHTML` injection avoids relying on SVG composables (which the rest
    // of the codebase doesn't import) and re-renders whenever the data string
    // changes via `key`.
    key(svg) {
        Div(
            attrs = {
                ref { el ->
                    el.innerHTML = svg
                    onDispose { }
                }
                style {
                    property("width", "100%")
                    property("height", "100%")
                    property("display", "block")
                }
            }
        )
    }
}

/** Absolute-positioned slot inside the back-side card, given percent offsets. */
@Composable
private fun BackAbsolute(
    leftPct: Double,
    topPct: Double,
    widthPct: Double? = null,
    heightPct: Double? = null,
    content: @Composable () -> Unit,
) {
    Box(
        modifier = Modifier.styleModifier {
            property("position", "absolute")
            property("left", "${(leftPct * 100)}%")
            property("top", "${(topPct * 100)}%")
            if (widthPct != null) property("width", "${(widthPct * 100)}%")
            if (heightPct != null) property("height", "${(heightPct * 100)}%")
        }
    ) {
        content()
    }
}

/** A single radar stat (icon + parenthesised value), centered. */
@Composable
private fun StatSlot(
    leftPct: Double,
    topPct: Double,
    slot: Pair<String, Int?>,
) {
    BackAbsolute(leftPct = leftPct, topPct = topPct) {
        Column(
            modifier = Modifier.sGap(2),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Icon(src = slot.first, modifier = Modifier.sSize(24))
            SpanText(
                "(${slot.second ?: "—"})",
                modifier = Modifier
                    .sFontSize(14)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .sLetterSpacing(0.028)
                    .color(AppColors.Text.Secondary)
                    .styleModifier { property("text-align", "center") }
            )
        }
    }
}

@Composable
private fun CountPill(
    label: String,
    count: Long,
    onClick: (() -> Unit)? = null,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .then(if (onClick != null) Modifier.cursor(Cursor.Pointer).onClick { onClick() } else Modifier)
            .styleModifier {
                property("position", "relative")
                property("border-radius", s(22))
                property("background", AppGradients.InteractiveSecondary)
                property("box-sizing", "border-box")
            },
        contentAlignment = Alignment.Center
    ) {
        PillGradientBorder(22)
        Row(
            modifier = Modifier
                .sGap(8)
                .styleModifier { property("padding", "${s(8)} ${s(16)}") },
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                label,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .sLetterSpacing(0.032)
                    .color(AppColors.Text.Secondary)
            )
            Box(
                modifier = Modifier
                    .sSize(20)
                    .sBorderRadius(100)
                    .backgroundColor(LocalSportColors.current.primary)
                    .overflow(Overflow.Hidden),
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    "$count",
                    modifier = Modifier
                        .sFontSize(14)
                        .fontWeight(AppTypography.Regular)
                        .color(LocalSportColors.current.onPrimary)
                        .styleModifier {
                            property("font-feature-settings", "\"tnum\"")
                            property("text-align", "center")
                            property("transform", "translateY(1px)")
                        }
                )
            }
        }
    }
}

// =============================================================================
// Player Honors panel (left-bottom)
// =============================================================================
@Composable
private fun PlayerHonorsPanel(modifier: Modifier = Modifier) {
    val t = LocalAppStrings.current
    Column(
        modifier = modifier
            .fillMaxWidth()
            .sBorderRadius(16)
            .styleModifier {
                property("padding", "${s(16)} ${s(12)} 0 ${s(12)}")
                property("background", INNER_BG)
                property("position", "relative")
            }
            .sGap(24)
    ) {
        InnerPanelBorderRing()
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                t.profileSidebarAchievement,
                modifier = Modifier
                    .sFontSize(24)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(32)
                    .sLetterSpacing(0.24)
                    .color(AppColors.Text.Secondary)
                    .flexGrow(1)
            )
            Icon(src = AppIcons.Ribbon, modifier = Modifier.sSize(24))
        }
        // Empty placeholder area — content comes later
    }
}

// =============================================================================
// Basic Info panel (right-top): name + age + location + languages + description
// =============================================================================
@Composable
internal fun BasicInfoPanel(
    user: UserDto?,
    isOwnProfile: Boolean,
    languages: List<String> = emptyList(),
    onEditClick: (() -> Unit)? = null,
    /** Optional slot that replaces the default bare-icon edit button.
     *  Receives the click handler; the caller is responsible for wiring it
     *  into the rendered button. Used by mobile to swap in a circular
     *  gradient-bordered button matching Figma 8428:86808. */
    renderEditButton: (@Composable (onClick: () -> Unit) -> Unit)? = null,
    /** When true, the inner panel chrome (rounded corner, padding,
     *  background gradient, border ring) is stripped so the content sits
     *  flush with whatever outer container the caller provides — used by
     *  mobile where the `ContainerCard` around the whole Overview block is
     *  the only background. */
    flat: Boolean = false,
) {
    val sportColors = LocalSportColors.current
    val nameAge = buildString {
        append(user?.fullName?.takeIf { it.isNotBlank() } ?: user?.username ?: "—")
        val age = computeAge(user?.dateOfBirth)
        if (age != null) append(", $age")
    }
    val location = listOfNotNull(user?.city, user?.country).joinToString(", ").ifEmpty { "—" }

    val hasDob = !user?.dateOfBirth.isNullOrBlank()
    val hasLocation = !user?.city.isNullOrBlank() || !user?.country.isNullOrBlank()
    val hasLanguages = languages.isNotEmpty()
    val hasBio = !user?.bio.isNullOrBlank()
    // Empty profile = no DOB, no location, no languages, no bio. Triggers the
    // "About you" empty state (Figma 8171:171660): zodiac slot, location row
    // and language row are all suppressed so only the name + edit pencil sit
    // above a centered call-to-action.
    val isProfileEmpty = !hasDob && !hasLocation && !hasLanguages && !hasBio

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .then(
                if (flat) Modifier
                else Modifier
                    .sBorderRadius(16)
                    .styleModifier {
                        property("padding", "${s(8)} ${s(12)} ${s(12)} ${s(12)}")
                        property("background", INNER_BG)
                        property("position", "relative")
                    }
            )
            // Flat mode tightens the outer gap so the bio sits close under
            // the location/languages line — matches the mobile Figma where
            // there's no large breathing room between them.
            .sGap(if (flat) 12 else 24)
    ) {
        if (!flat) InnerPanelBorderRing()

        // ── User Name section: name row (name | divider | zodiac | edit)
        //    stacked above location/languages, followed by the section
        //    divider when a bio exists. Wrapper Column keeps the divider
        //    visually attached to the user-name block (no extra outer gap)
        //    while the panel's sGap(24) still applies cleanly between the
        //    wrapper and the About text below.
        Column(modifier = Modifier.fillMaxWidth()) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .sGap(8)
                // No extra bottom padding in flat mode — the outer column's
                // sGap(12) already provides the right spacing before the
                // bio. In normal (chrome) mode the padding sits above the
                // gradient section divider as designed.
                .sPaddingBottom(if (isProfileEmpty || flat) 0 else 12)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().sGap(12),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    nameAge,
                    modifier = Modifier
                        .sFontSize(20)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(26)
                        .sLetterSpacing(0.2)
                        .color(com.varabyte.kobweb.compose.ui.graphics.Color.Companion.rgb(0x1E, 0x7F, 0xF5))
                )
                if (!isProfileEmpty) {
                    // Vertical divider — 1×26 grey line between the name and the
                    // zodiac sign slot (Figma 7861:353877).
                    Box(
                        modifier = Modifier
                            .sWidth(1)
                            .sHeight(26)
                            .styleModifier {
                                property("background", "rgba(148, 149, 152, 0.3)")
                            }
                    )
                    // Zodiac sign slot (Figma 7861:353878) — computed from DOB.
                    // When DOB is missing we still render the empty 44×44 outline
                    // so the row geometry stays stable.
                    ZodiacSignSlot(dateOfBirth = user?.dateOfBirth)
                }
                // Push the edit pencil to the right.
                Box(modifier = Modifier.flexGrow(1))
                if (isOwnProfile) {
                    val onClick: () -> Unit = { onEditClick?.invoke() }
                    if (renderEditButton != null) {
                        renderEditButton(onClick)
                    } else {
                        Box(
                            modifier = Modifier
                                .sSize(32)
                                .cursor(Cursor.Pointer)
                                .onClick { onClick() },
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(src = AppIcons.Pencil, modifier = Modifier.sSize(20))
                        }
                    }
                }
            }

            if (!isProfileEmpty) {
                // Location + Languages stacked, both muted #99B1BF.
                Column(modifier = Modifier.fillMaxWidth().sGap(4)) {
                    Row(
                        modifier = Modifier.sGap(6),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(src = AppIcons.Location, modifier = Modifier.sSize(16))
                        SpanText(
                            location,
                            modifier = Modifier
                                .sFontSize(14)
                                .fontWeight(AppTypography.Regular)
                                .sLineHeight(22)
                                .sLetterSpacing(0.028)
                                .color(AppColors.Text.OnSurfaceMuted)
                        )
                    }
                    if (languages.isNotEmpty()) {
                        Box(modifier = Modifier.sPaddingLeft(4)) {
                            SpanText(
                                "• ${languages.joinToString(", ")}",
                                modifier = Modifier
                                    .sFontSize(14)
                                    .fontWeight(AppTypography.Regular)
                                    .sLineHeight(22)
                                    .sLetterSpacing(0.028)
                                    .color(AppColors.Text.OnSurfaceMuted)
                            )
                        }
                    }
                }
            }
        }
            // Gradient section divider — same fade as the header divider in
            // the Create Post popup (90° linear-gradient transparent → 30%
            // grey → transparent). Suppressed in `flat` mode because the
            // mobile layout places this divider OUTSIDE the panel as the
            // separator between Basic Info and Focus & Activities sections.
            if (hasBio && !flat) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sHeight(1)
                        .styleModifier {
                            property(
                                "background",
                                "linear-gradient(90deg, rgba(148, 149, 152, 0) 0%, rgba(148, 149, 152, 0.3) 50%, rgba(148, 149, 152, 0) 100%)"
                            )
                        }
                )
            }
        }

        // ── About / bio — fixed 96px slot per Figma so the panel keeps the
        //    same height whether or not the bio is set. When the profile is
        //    completely empty, the slot grows to ~164px to compensate for
        //    the suppressed location + language rows (≈68px), so the panel
        //    total height stays close to the populated state and hosts the
        //    centered "About you" empty state (Figma 8171:171660).
        //
        //    In `flat` mode the fixed height is dropped — the mobile layout
        //    wants the section divider to sit directly under the bio (or
        //    under the empty-state CTA) instead of below a tall reserved
        //    slot.
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .then(
                    if (flat) Modifier
                    else Modifier.sHeight(if (isProfileEmpty) 164 else 96)
                ),
            contentAlignment = if (isProfileEmpty) Alignment.Center else Alignment.TopStart
        ) {
            if (isProfileEmpty) {
                AboutYouEmptyState()
            } else {
                SpanText(
                    user?.bio?.takeIf { it.isNotBlank() }.orEmpty(),
                    modifier = Modifier
                        .fillMaxWidth()
                        .sFontSize(16)
                        .fontWeight(AppTypography.Regular)
                        .sLineHeight(24)
                        .sLetterSpacing(0.16)
                        .color(AppColors.Text.Secondary)
                        .styleModifier {
                            property("text-align", "justify")
                            property("display", "-webkit-box")
                            property("-webkit-line-clamp", "4")
                            property("-webkit-box-orient", "vertical")
                            property("overflow", "hidden")
                        }
                )
            }
        }
    }
}

// =============================================================================
// "About you" empty state — Figma 8171:171660. Person glyph (Figma 8171:171682,
// matches AppIcons.Profile) on top of the title + supporting body, all stacked
// with a 6px gap and centered horizontally. The pencil icon in the parent panel
// header is the entry point, so this composable renders only the illustration.
// =============================================================================
@Composable
private fun AboutYouEmptyState() {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().sGap(6),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Box(modifier = Modifier.sSize(48), contentAlignment = Alignment.Center) {
            TintedGlyph(src = AppIcons.ProfileMuted, sizePx = 44)
        }
        SpanText(
            t.profileSidebarAboutYouEmpty,
            modifier = Modifier
                .fillMaxWidth()
                .sFontSize(16)
                .fontWeight(AppTypography.Regular)
                .sLineHeight(24)
                .sLetterSpacing(0.16)
                .color(AppColors.Text.Secondary)
                .styleModifier {
                    property("text-align", "center")
                }
        )
    }
}

@Composable
private fun ZodiacSignSlot(dateOfBirth: String?) {
    // 44×44 circle with just the zodiac glyph centered (Figma 7965:189363).
    // Border uses the mask-composite gradient ring — sub-pixel `0.3px solid`
    // was rendering invisibly. When DOB is missing, the empty outlined slot
    // still holds the row geometry.
    val sign = computeZodiacSign(dateOfBirth)
    Box(
        modifier = Modifier
            .sSize(44)
            .sBorderRadius(100)
            .styleModifier {
                property("position", "relative")
                property(
                    "background",
                    AppGradients.InteractiveSecondary
                )
            },
        contentAlignment = Alignment.Center
    ) {
        PillGradientBorder(radius = 100)
        if (sign != null) {
            Icon(src = sign.iconPath, modifier = Modifier.sSize(18))
        }
    }
}

private data class ZodiacSign(val label: String, val iconPath: String)

/**
 * Standard astrological date ranges. Inputs accepted: ISO `YYYY-MM-DD`
 * (with or without `T…` suffix). Returns null on a missing/unparseable DOB.
 */
private fun computeZodiacSign(dob: String?): ZodiacSign? {
    if (dob.isNullOrBlank()) return null
    val datePart = dob.substringBefore('T').trim()
    val parts = datePart.split('-')
    if (parts.size != 3) return null
    val month = parts[1].toIntOrNull() ?: return null
    val day = parts[2].toIntOrNull() ?: return null
    return when {
        (month == 3 && day >= 21) || (month == 4 && day <= 19) -> ZodiacSign("aries", AppIcons.ZodiacAries)
        (month == 4 && day >= 20) || (month == 5 && day <= 20) -> ZodiacSign("taurus", AppIcons.ZodiacTaurus)
        (month == 5 && day >= 21) || (month == 6 && day <= 20) -> ZodiacSign("gemini", AppIcons.ZodiacGemini)
        (month == 6 && day >= 21) || (month == 7 && day <= 22) -> ZodiacSign("cancer", AppIcons.ZodiacCancer)
        (month == 7 && day >= 23) || (month == 8 && day <= 22) -> ZodiacSign("leo", AppIcons.ZodiacLeo)
        (month == 8 && day >= 23) || (month == 9 && day <= 22) -> ZodiacSign("virgo", AppIcons.ZodiacVirgo)
        (month == 9 && day >= 23) || (month == 10 && day <= 22) -> ZodiacSign("libra", AppIcons.ZodiacLibra)
        (month == 10 && day >= 23) || (month == 11 && day <= 21) -> ZodiacSign("scorpio", AppIcons.ZodiacScorpio)
        (month == 11 && day >= 22) || (month == 12 && day <= 21) -> ZodiacSign("sagittarius", AppIcons.ZodiacSagittarius)
        (month == 12 && day >= 22) || (month == 1 && day <= 19) -> ZodiacSign("capricorn", AppIcons.ZodiacCapricorn)
        (month == 1 && day >= 20) || (month == 2 && day <= 18) -> ZodiacSign("aquarius", AppIcons.ZodiacAquarius)
        (month == 2 && day >= 19) || (month == 3 && day <= 20) -> ZodiacSign("pisces", AppIcons.ZodiacPisces)
        else -> null
    }
}

private fun computeAge(dob: String?): Int? {
    if (dob.isNullOrBlank()) return null
    return try {
        val date = kotlin.js.Date(dob)
        val now = kotlin.js.Date()
        val years = now.getFullYear().toInt() - date.getFullYear().toInt()
        val monthDiff = now.getMonth().toInt() - date.getMonth().toInt()
        val dayDiff = now.getDate().toInt() - date.getDate().toInt()
        if (monthDiff < 0 || (monthDiff == 0 && dayDiff < 0)) years - 1 else years
    } catch (_: Throwable) { null }
}

// =============================================================================
// Interests panel (right-bottom): Sport / Activity / Lifestyle pills + Follow/Chat buttons
// =============================================================================
@Composable
internal fun InterestsPanel(
    isOwnProfile: Boolean,
    isFollowing: Boolean,
    onFollowClick: (() -> Unit)?,
    onChatClick: (() -> Unit)?,
    onEditClick: (() -> Unit)? = null,
    interestSports: Set<String> = emptySet(),
    interestActivities: Set<String> = emptySet(),
    interestLifestyle: Set<String> = emptySet(),
    interestNotes: Map<String, String> = emptyMap(),
    onInterestNoteClick: ((String) -> Unit)? = null,
    /** Optional slot that replaces the default bare-icon edit button —
     *  see BasicInfoPanel for usage. */
    renderEditButton: (@Composable (onClick: () -> Unit) -> Unit)? = null,
    /** See BasicInfoPanel.flat — strips the inner panel chrome. */
    flat: Boolean = false,
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Column(
        modifier = modifier
            .fillMaxWidth()
            .then(
                if (flat) Modifier
                else Modifier
                    .sBorderRadius(16)
                    .sPadding(12)
                    .styleModifier {
                        property("background", INNER_BG)
                        property("position", "relative")
                    }
            )
            .sGap(18)
    ) {
        if (!flat) InnerPanelBorderRing()
        // Title + edit affordance
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                t.profileSidebarFocusActivities,
                modifier = Modifier
                    .sFontSize(20)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(26)
                    .sLetterSpacing(0.2)
                    .color(com.varabyte.kobweb.compose.ui.graphics.Color.Companion.rgb(0x1E, 0x7F, 0xF5))
                    .flexGrow(1)
            )
            if (isOwnProfile) {
                val onClick: () -> Unit = { onEditClick?.invoke() }
                if (renderEditButton != null) {
                    renderEditButton(onClick)
                } else {
                    Box(
                        modifier = Modifier
                            .sSize(32)
                            .cursor(Cursor.Pointer)
                            .onClick { onClick() },
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(src = AppIcons.Pencil, modifier = Modifier.sSize(20))
                    }
                }
            }
        }

        val hasInterests = interestSports.isNotEmpty() ||
            interestActivities.isNotEmpty() ||
            interestLifestyle.isNotEmpty()

        if (hasInterests) {
            // Interests List — gap-32 between Sport / Activity / Lifestyle sections.
            // Each section renders only when the user has picked at least one item.
            Column(modifier = Modifier.fillMaxWidth().sGap(32)) {
                if (interestSports.isNotEmpty()) {
                    InterestSection(label = t.profileSidebarSportLabel) {
                        interestSports.forEach { id ->
                            val opt = InterestCatalog.lookup(id)
                            val hasNote = !interestNotes[id].isNullOrBlank()
                            ChipPill(
                                label = opt?.label ?: id.replaceFirstChar { it.uppercase() },
                                icon = opt?.iconSrc ?: AppIcons.ballSmall(id),
                                hasNote = hasNote,
                                onClick = if (hasNote) ({ onInterestNoteClick?.invoke(id) }) else null
                            )
                        }
                    }
                }
                if (interestActivities.isNotEmpty()) {
                    InterestSection(label = t.profileSidebarActivityLabel) {
                        interestActivities.forEach { id ->
                            val opt = InterestCatalog.lookup(id)
                            val hasNote = !interestNotes[id].isNullOrBlank()
                            ChipPill(
                                label = opt?.label ?: id,
                                icon = opt?.iconSrc,
                                hasNote = hasNote,
                                onClick = if (hasNote) ({ onInterestNoteClick?.invoke(id) }) else null
                            )
                        }
                    }
                }
                if (interestLifestyle.isNotEmpty()) {
                    InterestSection(label = t.profileSidebarLifestyleLabel) {
                        interestLifestyle.forEach { id ->
                            val opt = InterestCatalog.lookup(id)
                            val hasNote = !interestNotes[id].isNullOrBlank()
                            ChipPill(
                                label = opt?.label ?: id,
                                icon = opt?.iconSrc,
                                hasNote = hasNote,
                                onClick = if (hasNote) ({ onInterestNoteClick?.invoke(id) }) else null
                            )
                        }
                    }
                }
            }

            // Bottom action area
            Box(modifier = Modifier.flexGrow(1))
        } else {
            // Empty state (Figma 8171:171688) — grid icon + title + body, all
            // centered with a 40px top inset; the column expands to fill the
            // panel so action buttons (for foreign profiles) stay pinned to
            // the bottom.
            InterestsEmptyState(modifier = Modifier.fillMaxWidth().flexGrow(1))
        }

        if (!isOwnProfile) {
            // When following: blue "Chat" sits ABOVE an outlined "Unfollow"
            // — messaging is the primary action once a relationship exists,
            // unfollowing is a secondary affordance.
            // When NOT following: only the blue "Follow" pill is shown.
            Column(
                modifier = Modifier.fillMaxWidth().sGap(8),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                if (isFollowing && onChatClick != null) {
                    ActionPillButton(
                        label = t.profileSidebarChat,
                        onClick = { onChatClick() },
                        primary = true
                    )
                }
                ActionPillButton(
                    label = if (isFollowing) t.profileSidebarUnfollow else t.profileSidebarFollow,
                    onClick = { onFollowClick?.invoke() },
                    primary = !isFollowing
                )
            }
        }
    }
}

// =============================================================================
// "No interests added" empty state — Figma 8171:171688. Grid glyph (56×56) on
// top, "No interests added" headline, supporting body. 40px top inset, 12px
// gap between icon and text block, 6px gap inside the text block. The pencil
// in the parent header is the entry point; this composable renders only the
// centered illustration + copy.
// =============================================================================
@Composable
private fun InterestsEmptyState(modifier: Modifier = Modifier) {
    val t = LocalAppStrings.current
    Column(
        modifier = modifier.sPaddingTop(40).sGap(12),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Box(modifier = Modifier.sSize(56), contentAlignment = Alignment.Center) {
            TintedGlyph(src = AppIcons.GridFilled, sizePx = 49)
        }
        Column(
            modifier = Modifier.fillMaxWidth().sGap(6),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            SpanText(
                t.profileSidebarNoInterests,
                modifier = Modifier
                    .sFontSize(20)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(26)
                    .sLetterSpacing(0.2)
                    .color(AppColors.Text.Secondary)
            )
            SpanText(
                t.profileSidebarNoInterestsBody,
                modifier = Modifier
                    .fillMaxWidth()
                    .sFontSize(16)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(24)
                    .sLetterSpacing(0.16)
                    .color(AppColors.Text.Secondary)
                    .styleModifier { property("text-align", "center") }
            )
        }
    }
}

@Composable
private fun InterestSection(label: String, content: @Composable () -> Unit) {
    Column(modifier = Modifier.fillMaxWidth().sGap(18)) {
        SpanText(
            "$label:",
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Light)
                .sLineHeight(18)
                .sLetterSpacing(0.028)
                .color(AppColors.Text.Secondary)
        )
        Row(
            modifier = Modifier.fillMaxWidth().sGap(8).styleModifier {
                property("flex-wrap", "wrap")
                property("row-gap", s(8))
            },
            verticalAlignment = Alignment.CenterVertically
        ) {
            content()
        }
    }
}

@Composable
private fun ChipPill(
    label: String,
    icon: String? = null,
    hasNote: Boolean = false,
    onClick: (() -> Unit)? = null
) {
    val sport = LocalSportColors.current
    Box(
        modifier = Modifier
            .sHeight(32)
            .then(if (onClick != null) Modifier.cursor(Cursor.Pointer).onClick { onClick() } else Modifier)
            .styleModifier {
                property("position", "relative")
                property("border-radius", s(22))
                property("background", AppGradients.InteractiveSecondary)
            }
    ) {
        PillGradientBorder(22)
        Row(
            modifier = Modifier
                .fillMaxHeight()
                .sPadding(0, 14)
                .sGap(8),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (icon != null) Icon(src = icon, modifier = Modifier.sSize(16))
            SpanText(
                label,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .color(AppColors.Text.Secondary)
            )
        }
        // Note indicator (Figma 7861:353977) — 8×8 blue dot at top-right corner
        // when this interest has a saved preference note. Click on the whole chip
        // opens the read-only details dialog.
        if (hasNote) {
            Box(
                modifier = Modifier
                    .sSize(8)
                    .styleModifier {
                        property("position", "absolute")
                        property("top", s(-1))
                        property("right", s(-1))
                        property("background-color", sport.primary.toRgb().let {
                            "rgb(${it.red}, ${it.green}, ${it.blue})"
                        })
                        property("border", "0.5px solid #081735")
                        property("border-radius", "100px")
                        property("z-index", "1")
                        property("pointer-events", "none")
                    }
            )
        }
    }
}

@Composable
private fun ActionPillButton(
    label: String,
    onClick: () -> Unit,
    primary: Boolean
) {
    val sportColors = LocalSportColors.current
    Box(
        modifier = Modifier
            .sWidth(185)
            .sHeight(46)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("position", "relative")
                property("border-radius", s(100))
                if (primary) {
                    property("background", sportColors.primary.toRgb().let {
                        "rgb(${it.red}, ${it.green}, ${it.blue})"
                    })
                } else {
                    property("background", AppGradients.InteractiveSecondary)
                }
            },
        contentAlignment = Alignment.Center
    ) {
        if (!primary) PillGradientBorder(100)
        SpanText(
            label,
            modifier = Modifier
                .sFontSize(16)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(18)
                .sLetterSpacing(0.032)
                .color(if (primary) sportColors.onPrimary else AppColors.Text.Secondary)
        )
    }
}

/**
 * Mask-composite gradient border for pills / interactive buttons. Parent must be
 * `position: relative` with matching border-radius.
 */
@Composable
private fun PillGradientBorder(radius: Number) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                property("position", "absolute")
                property("inset", "0")
                property("border", "0.3px solid transparent")
                property("border-radius", s(radius))
                property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("-webkit-mask-composite", "xor")
                property("mask-composite", "exclude")
                property("pointer-events", "none")
            }
    )
}

// Local helper — gradient border ring for inner panels.
// Caller's container must have position:relative and matching border-radius.
@Composable
private fun InnerPanelBorderRing(radius: Number = 16) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                property("position", "absolute")
                property("inset", "0")
                property("border", "1px solid transparent")
                property("border-radius", s(radius))
                property("background", "$INNER_BORDER_GRADIENT border-box")
                property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("-webkit-mask-composite", "xor")
                property("mask-composite", "exclude")
                property("pointer-events", "none")
            }
    )
}




